import React from 'react';
import { Field } from 'redux-form';
import { Grid, Header } from 'semantic-ui-react';
import PropTypes from 'prop-types';

import {
  InputField,
  SelectField,
  TextLabelField,
  DatePickerField,
  DropdownField,
} from 'Common/SemanticWrapper';
import ReactTable from 'Common/ReactTableWrapper';
import { normalizeUnit } from 'Common/FormHelper';
import {
  ToolTipComponent,
  TitledCard,
  DatePickerCustomButton,
} from 'Components';
import { TableNamesEnum } from 'Common/Constants/Enumeration/TableEnum';
import { BidPriceEnum, UpdateBidPriceDetailsEnum } from 'Common/FieldEnum';
import {
  makeMeasureOptions,
  makeUnitsOptions,
  makeOptionsDataWithEmpty,
} from 'Common/FormHelper';
import { DATE_FORMAT_FOR_UI } from 'Common/Constants';
import { GridEnum } from 'Common/Constants/Enumeration/GridEnum';
import { PACK_SIZE_MESSAGE } from 'Common/Constants/inventory';
import { BracketTypeEnum } from 'Common/Constants/Enumeration/ItemMaintenanceEnum';

import {
  getGramConversion,
  normalizeInput,
  bracketTypeOptions,
} from '../helpers';
import LevelContainer from './../containers/LevelContainer';

const UpdateBidPriceDetailsComponent = props => {
  const {
    bidPriceDetailsData,
    loadingControlInfo,
    columns,
    onRowClick,
    selectedBidControlInfo,
    loadingBidPriceDetails,
    handleChange,
    addNewPurchaseUnit,
    addNewSecUnit,
    gridPreferenceLoading,
    gridPreferenceSubmitting,
    primaryPrice,
    resetPageOnDataChange,
    onResetPageOnDataChange,
    secondaryUnitWeight,
    secondaryUnitMeasure,
    loadingWarehouseWarning,
    bracketType,
    handleLevelChange,
  } = props;

  const vendorDataWithEmpty =
    bidPriceDetailsData &&
    bidPriceDetailsData[UpdateBidPriceDetailsEnum.Vendors]
      ? makeOptionsDataWithEmpty(
          bidPriceDetailsData[UpdateBidPriceDetailsEnum.Vendors]
        )
      : [];
  const secUnitMeasuresOptions =
    bidPriceDetailsData &&
    bidPriceDetailsData[UpdateBidPriceDetailsEnum.SecUnitMeasures]
      ? makeMeasureOptions(
          bidPriceDetailsData[UpdateBidPriceDetailsEnum.SecUnitMeasures]
        )
      : [];
  const unitOptions =
    bidPriceDetailsData && bidPriceDetailsData[UpdateBidPriceDetailsEnum.Units]
      ? makeUnitsOptions(bidPriceDetailsData[UpdateBidPriceDetailsEnum.Units])
      : [];

  const secondaryUnitsOptions =
    bidPriceDetailsData &&
    bidPriceDetailsData[UpdateBidPriceDetailsEnum.SecondaryUnits]
      ? makeUnitsOptions(
          bidPriceDetailsData[UpdateBidPriceDetailsEnum.SecondaryUnits]
        )
      : [];
  return (
    <React.Fragment>
      <TitledCard>
        <Grid>
          <Grid.Row columns={1} className={'remove-spacing'}>
            <Grid.Column>
              <ReactTable
                loading={
                  loadingControlInfo ||
                  loadingBidPriceDetails ||
                  gridPreferenceLoading ||
                  gridPreferenceSubmitting ||
                  loadingWarehouseWarning
                }
                defaultPageSize={20}
                showPageSizeOptions={true}
                minRows={0}
                data={
                  bidPriceDetailsData && bidPriceDetailsData.BidPriceDetails
                    ? bidPriceDetailsData.BidPriceDetails
                    : []
                }
                noDataText={loadingBidPriceDetails ? '' : 'No items found'}
                resizable={false}
                className="-striped -highlight"
                getTrProps={onRowClick}
                columns={columns}
                draggableColumns={true}
                showHideColumn={true}
                menu={'hasCogs'}
                saveState={{
                  tableName: TableNamesEnum.UPDATE_BID_PRICE_DETAILS_TABLE,
                }}
                resetPageOnDataChange={resetPageOnDataChange}
                onResetPageOnDataChange={onResetPageOnDataChange}
                gridName={GridEnum.INV_BidPrice_View}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </TitledCard>
      <Grid className="grid-spacing">
        <Grid.Row columns={1}>
          <Grid.Column>
            <span className={'bidPriceItemName'}>Item:</span>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column>
            <Header as="h4" className={'text-orange'}>
              <Field
                name={BidPriceEnum.BidItemDesc}
                component={TextLabelField}
                disabled={true}
              />
            </Header>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row columns={4}>
          <Grid.Column>
            <Field
              component={DatePickerField}
              label={'Begin Date:'}
              dateFormat={DATE_FORMAT_FOR_UI}
              name={BidPriceEnum.BeginDT}
              type={'text'}
              onChange={handleChange}
              customInput={<DatePickerCustomButton />}
              dropdownMode="select"
            />
          </Grid.Column>
          <Grid.Column>
            <Field
              component={DatePickerField}
              label={'End Date:'}
              dateFormat={DATE_FORMAT_FOR_UI}
              name={UpdateBidPriceDetailsEnum.BidPriceEndDate}
              type={'text'}
              onChange={handleChange}
              customInput={<DatePickerCustomButton />}
              dropdownMode="select"
            />
          </Grid.Column>
          <Grid.Column>
            <Field
              component={SelectField}
              label={'Bracket Type:'}
              name={UpdateBidPriceDetailsEnum.BracketType}
              options={bracketTypeOptions}
              onChange={handleChange}
              type="text"
            />
          </Grid.Column>
          <Grid.Column>
            <Field
              name={BidPriceEnum.PrimaryPrice}
              component={InputField}
              maxLength={8}
              type="text"
              label={'Bid Price: $'}
              onChange={handleChange}
              value={primaryPrice}
              disabled={
                (selectedBidControlInfo &&
                  !selectedBidControlInfo[
                    UpdateBidPriceDetailsEnum.PrimaryPriceTextEnabled
                  ]) ||
                bracketType !== BracketTypeEnum.None
              }
              normalize={value =>
                normalizeInput(value, BidPriceEnum.PrimaryPrice)
              }
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row columns={1}>
          <Grid.Column>
            <Field
              component={LevelContainer}
              name={UpdateBidPriceDetailsEnum.BracketPricingDetails}
              onChange={handleChange}
              {...{
                handleLevelChange,
                primaryPrice,
                selectedBidControlInfo,
                disabled: bracketType === BracketTypeEnum.None,
              }}
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row columns={4}>
          <Grid.Column>
            <Field
              name={BidPriceEnum.Unit}
              component={DropdownField}
              search
              selection
              fluid
              allowAdditions
              options={unitOptions}
              onChange={handleChange}
              type="text"
              maxLength={15}
              onAddItem={addNewPurchaseUnit}
              normalize={normalizeUnit}
              label={'Purchase Unit:'}
              disabled={
                selectedBidControlInfo &&
                !selectedBidControlInfo[
                  UpdateBidPriceDetailsEnum.UnitComboEnabled
                ]
              }
            />
          </Grid.Column>
          <Grid.Column>
            <Field
              component={InputField}
              label={
                <ToolTipComponent
                  label={'Pack Size'}
                  popMessage={PACK_SIZE_MESSAGE}
                  iconName="question circle outline"
                />
              }
              name={BidPriceEnum.QtyPerUnit}
              type={'text'}
              onChange={handleChange}
              maxLength={5}
              disabled={
                selectedBidControlInfo &&
                !selectedBidControlInfo[
                  UpdateBidPriceDetailsEnum.QtyPerUnitTextEnabled
                ]
              }
              normalize={value =>
                normalizeInput(value, BidPriceEnum.QtyPerUnit)
              }
            />
          </Grid.Column>
          <Grid.Column>
            <Field
              component={DropdownField}
              label={'Pack Unit:'}
              name={BidPriceEnum.SecondaryUnit}
              search
              selection
              fluid
              allowAdditions
              options={secondaryUnitsOptions}
              type={'text'}
              maxLength={15}
              onAddItem={addNewSecUnit}
              normalize={normalizeUnit}
              disabled={
                selectedBidControlInfo &&
                !selectedBidControlInfo[
                  UpdateBidPriceDetailsEnum.SecUnitComboEnabled
                ]
              }
              onChange={handleChange}
            />
          </Grid.Column>
          <Grid.Column>
            <Field
              name={BidPriceEnum.VendorId}
              component={SelectField}
              type={'text'}
              options={vendorDataWithEmpty}
              label={'Distributor:'}
              maxLength={15}
              onChange={handleChange}
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row columns={4}>
          <Grid.Column>
            <Field
              component={InputField}
              label={'Distributor Item:'}
              name={BidPriceEnum.VendorItem}
              type={'text'}
              maxLength={15}
              onChange={handleChange}
            />
          </Grid.Column>
          <Grid.Column>
            <Field
              name={BidPriceEnum.GTINNumber}
              component={InputField}
              type="text"
              label={'GTIN Number : '}
              maxLength={14}
              onChange={handleChange}
            />
          </Grid.Column>
          <Grid.Column>
            <Field
              component={InputField}
              label={'Brand:'}
              name={BidPriceEnum.CurrentBrand}
              type={'text'}
              maxLength={15}
              onChange={handleChange}
            />
          </Grid.Column>
          <Grid.Column>
            <Field
              component={InputField}
              label={'UPC:'}
              name={BidPriceEnum.UPC}
              type={'text'}
              maxLength={12}
              onChange={handleChange}
            />
          </Grid.Column>
        </Grid.Row>
        <TitledCard>
          <Grid>
            <Grid.Row columns={2}>
              <Grid.Column>
                <Header as="h4" className={'text-orange'}>
                  Bid Analysis
                </Header>
              </Grid.Column>
              <Grid.Column>
                <Header as="h4" className={'text-orange'}>
                  Menu Costing
                </Header>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row columns={4}>
              <Grid.Column>
                <Field
                  component={InputField}
                  label={'Servings Per Purchase Unit:'}
                  name={BidPriceEnum.ServingsPerUnit}
                  type={'text'}
                  maxLength={5}
                  onChange={handleChange}
                  normalize={value =>
                    normalizeInput(value, BidPriceEnum.ServingsPerUnit)
                  }
                />
              </Grid.Column>
              <Grid.Column>
                <Field
                  component={InputField}
                  label={'Cost Per Serving: $'}
                  name={BidPriceEnum.CostPerServing}
                  type={'text'}
                  maxLength={15}
                  disabled={true}
                />
              </Grid.Column>
              <Grid.Column>
                <Field
                  component={InputField}
                  label={'Pack Weight:'}
                  name={BidPriceEnum.SecondaryUnitWeight}
                  type={'text'}
                  maxLength={7}
                  onChange={handleChange}
                  normalize={value =>
                    normalizeInput(value, BidPriceEnum.SecondaryUnitWeight)
                  }
                />
              </Grid.Column>
              <Grid.Column>
                <Field
                  name={BidPriceEnum.SecondaryUnitMeasure}
                  component={SelectField}
                  type={'text'}
                  options={secUnitMeasuresOptions}
                  label={'Measure:'}
                  maxLength={15}
                  onChange={handleChange}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row columns={4}>
              <Grid.Column />
              <Grid.Column />
              <Grid.Column />
              <Grid.Column>
                = {getGramConversion(secondaryUnitWeight, secondaryUnitMeasure)}
                grams
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </TitledCard>
      </Grid>
    </React.Fragment>
  );
};

UpdateBidPriceDetailsComponent.propTypes = {
  bidPriceDetailsData: PropTypes.object.isRequired,
  columns: PropTypes.array.isRequired,
  selectedBidControlInfo: PropTypes.object,
  onRowClick: PropTypes.func,
  handleChange: PropTypes.func,
  addNewPurchaseUnit: PropTypes.func,
  addNewSecUnit: PropTypes.func,
  savePreference: PropTypes.func,
  onResetPageOnDataChange: PropTypes.func,
  loadingControlInfo: PropTypes.bool,
  loadingBidPriceDetails: PropTypes.bool,
  gridPreferenceLoading: PropTypes.bool,
  gridPreferenceSubmitting: PropTypes.bool,
  resetPageOnDataChange: PropTypes.bool,
  loadingWarehouseWarning: PropTypes.bool,
  primaryPrice: PropTypes.string,
  secondaryUnitWeight: PropTypes.string,
  secondaryUnitMeasure: PropTypes.string,
  handleBracketTypeChange: PropTypes.func,
  bracketType: PropTypes.number,
  handleLevelChange: PropTypes.func,
};

UpdateBidPriceDetailsComponent.defaultProps = {
  selectedBidControlInfo: null,
  onRowClick: () => {},
  handleChange: () => {},
  addNewSecUnit: () => {},
  savePreference: () => {},
  primaryPrice: '0.0000',
  loadingBidPriceDetails: false,
  addNewPurchaseUnit: () => {},
  loadingControlInfo: false,
  gridPreferenceLoading: false,
  gridPreferenceSubmitting: false,
  onResetPageOnDataChange: () => {},
  resetPageOnDataChange: true,
  secondaryUnitWeight: '',
  loadingWarehouseWarning: false,
  secondaryUnitMeasure: '',
  handleBracketTypeChange: () => {},
  bracketType: BracketTypeEnum.None,
  handleLevelChange: () => {},
};
export default UpdateBidPriceDetailsComponent;



// WEBPACK FOOTER //
// ./src/pages/inventory/itemmaintenance/components/UpdateBidPriceDetails.js